/**
 * <card-quick-add>
 *
 * Inline "one-click add to cart" panel for product cards. It lets a shopper pick a size directly on the collection
 * card and add to cart.
 *
 * The panel is not server-rendered with the card (it weighed ~30 KB per card). It is fetched from
 * sections/card-quick-add.liquid on the first hover/focus of the "See options" bar, or on click at the latest.
 *
 * Two selection modes are supported:
 *   1. Simple single option  -> `.card-quick-add__value` buttons, each carrying `data-variant-id`.
 *   2. Split Waist/Length     -> the theme's `split-size-picker.js` drives `.split-size-picker__value` buttons and
 *                                checks a hidden radio (carrying `data-variant-id`).
 */
class CardQuickAdd extends HTMLElement {
  connectedCallback() {
    // Cards can be moved in the DOM (e.g. load more), only bind once
    if (this.toggle) {
      return;
    }

    this.toggle = this.querySelector('[data-quick-add-toggle]');

    this.toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });

    // Warm the panel as soon as the shopper shows intent, so the click usually opens it instantly
    const preload = () => this.load().catch(() => {});
    this.toggle?.addEventListener('pointerenter', preload, { once: true });
    this.toggle?.addEventListener('focus', preload, { once: true });

    // Collapse the panel once the item is successfully added to the cart (event bubbles up from the form).
    this.addEventListener('variant:add', () => this.close());
  }

  load() {
    if (!this.loadPromise) {
      this.loadPromise = fetch(this.getAttribute('section-url'))
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Quick add panel request failed (${response.status})`);
          }

          return response.text().then((html) => this.renderPanel(html, response.url));
        })
        .catch((error) => {
          // Allow a retry on the next attempt
          this.loadPromise = null;
          console.warn('[card-quick-add]', error);
          throw error;
        });
    }

    return this.loadPromise;
  }

  renderPanel(html, responseUrl) {
    const document_ = new DOMParser().parseFromString(html, 'text/html');

    // Only ever inject the panel section itself. A request that does not reach the section (a redirect dropping the
    // query string, an error page...) answers with the whole product page, and its <product-form> is the product
    // page's buy form: pasting that into the card shows a stray "Add to cart" button and breaks the card layout.
    const panel = document_.querySelector('#shopify-section-card-quick-add .card-quick-add__panel');
    const productForm = panel?.closest('product-form');

    if (!productForm) {
      throw new Error(`Quick add panel missing from the response for ${responseUrl}`);
    }

    // Drop anything injected earlier (or served in a cached page) so the card keeps a single panel
    this.querySelectorAll('product-form').forEach((stalePanel) => stalePanel.remove());

    panel.hidden = true;
    this.append(productForm);

    // Bind synchronously: split-size-picker.js initialises the appended picker from a MutationObserver (a microtask
    // later), and the "change" it may dispatch on its default radio must find our listeners.
    this.bindPanel();
  }

  bindPanel() {
    this.panel = this.querySelector('.card-quick-add__panel');
    this.idInput = this.querySelector('input[name="id"]');
    this.atc = this.querySelector('[data-quick-add-atc]');

    this.hasSelection = false;
    this.currentVariantId = this.idInput ? this.idInput.value : null;
    this.currentAvailable = null;

    this.querySelector('[data-quick-add-close]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });

    // Simple single-option value buttons.
    this.querySelectorAll('.card-quick-add__value').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.hasAttribute('disabled')) {
          return;
        }

        this.querySelectorAll('.card-quick-add__value').forEach((other) => {
          other.classList.remove('is-selected');
          other.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');

        this.select(button.dataset.variantId, button.dataset.available !== 'false');
      });
    });

    // Hidden radios (split-size) tell us which variant is currently selected.
    this.querySelectorAll('input[data-variant-id]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) {
          return;
        }

        this.select(radio.dataset.variantId, radio.dataset.available !== 'false');
      });
    });

    // Split-size opens with a default size already selected, so reflect it in the button right away. The simple size
    // grid has nothing pre-selected, so the button stays a "choose a size" prompt until the shopper picks one.
    const preselected = this.querySelector('input[data-variant-id]:checked');

    if (preselected) {
      this.select(preselected.dataset.variantId, preselected.dataset.available !== 'false');
    } else {
      this.refreshAtc();
    }
  }

  async open() {
    if (!this.panel) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        await this.load();
      } catch (error) {
        // Never leave the shopper with a dead button: fall back to the card's quick buy modal ("+"), then to the
        // product page
        const card = this.closest('product-card');
        const quickBuyButton = card?.querySelector('.product-card__quick-add-button[aria-controls]');

        if (quickBuyButton) {
          quickBuyButton.click();
          return;
        }

        const productLink = card?.querySelector('a.product-card__media');

        if (productLink) {
          window.location.href = productLink.href;
        }

        return;
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    this.classList.add('is-open');
    this.panel?.removeAttribute('hidden');
    this.toggle?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.panel?.setAttribute('hidden', '');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Record the currently selected variant and keep the form's hidden id input in sync.
   */
  select(variantId, available) {
    if (variantId) {
      this.currentVariantId = variantId;

      if (this.idInput) {
        this.idInput.value = variantId;
      }
    }

    this.currentAvailable = available;
    this.hasSelection = true;
    this.refreshAtc();
  }

  /**
   * Update the add-to-cart button label / disabled state, reflecting the currently selected size.
   */
  refreshAtc() {
    if (!this.atc) {
      return;
    }

    if (!this.hasSelection) {
      this.atc.disabled = true;
      this.atc.textContent = this.atc.dataset.labelSelect;
      return;
    }

    if (this.currentAvailable === false) {
      this.atc.disabled = true;
      this.atc.textContent = this.atc.dataset.labelSoldout;
      return;
    }

    this.atc.disabled = false;
    this.atc.textContent = this.atc.dataset.labelAdd;
  }
}

if (!window.customElements.get('card-quick-add')) {
  window.customElements.define('card-quick-add', CardQuickAdd);
}
